import { KdsActionId } from './KdsAuthorization'

export type PinExchangeSuccess = {
  outcome: 'success'
  accessToken: string
  expiresIn: number
}

export type PinExchangeFailure =
  | { outcome: 'invalidPin' }
  | { outcome: 'ineligibleOperator' }
  | { outcome: 'cooldown'; retryAfterSeconds: number }
  | { outcome: 'transportError' }

export type PinExchangeResult = PinExchangeSuccess | PinExchangeFailure

export type TokenResponseBody = {
  access_token?: string
  expires_in?: number
}

// invalid_grant/unauthorized_client are the RFC 6749 token-endpoint error codes; repurposed
// here for wrong-PIN vs operator-not-permitted-for-this-scope since this is a custom grant.
export type TokenErrorBody = {
  error?: 'invalid_grant' | 'unauthorized_client' | string
  retry_after?: number
}

/**
 * Handed to an elevated action's callback for the duration of that single call. Transient by
 * design -- never stored on an observable, never logged.
 */
export type KdsStepUpAuthorization = {
  token: string
  expiresAt: number
  /** Best-effort from the token claim; undefined means "ask the backend, it's authoritative". */
  allowedActions: readonly KdsActionId[] | undefined
}

export enum KdsAuthorizationErrorReason {
  InvalidPin = 'invalidPin',
  IneligibleOperator = 'ineligibleOperator',
  Cooldown = 'cooldown',
  TransportError = 'transportError',
  Cancelled = 'cancelled',
}

// Only a cooldown carries a duration; every other reason is just its tag.
export type KdsAuthorizationError =
  | { reason: Exclude<KdsAuthorizationErrorReason, KdsAuthorizationErrorReason.Cooldown> }
  | { reason: KdsAuthorizationErrorReason.Cooldown; retryAfterSeconds: number }

/**
 * Thrown when the operator dismisses the PIN prompt or the flow is torn down (logout, device
 * reset). Lets callers tell an intentional abort from a real failure without string-matching.
 */
export class KdsAuthorizationCancelledError extends Error {
  constructor(reason: string) {
    super(`Authorization cancelled: ${reason}`)
    this.name = 'KdsAuthorizationCancelledError'
  }
}
